import {
  forwardRef,
  useImperativeHandle,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  useTransition,
} from 'react';
import { Box3, Camera, Group, Matrix4, Object3D, Quaternion, Vector3 } from 'three';

import { Text } from '@react-three/drei';
import { context, OnDragStartProps } from '@react-three/drei/web/pivotControls/context.js';
import { Size, useFrame, useThree } from '@react-three/fiber';

import AxisArrow from './AxisArrow.js';
import AxisRotator from './AxisRotator.js';
import PlaneSlider from './PlaneSlider.js';

const tV0 = new Vector3();
const tV1 = new Vector3();
const tV2 = new Vector3();

const getPoint2 = (point3: Vector3, camera: Camera, size: Size) => {
  const widthHalf = size.width / 2;
  const heightHalf = size.height / 2;
  camera.updateMatrixWorld(false);
  const vector = point3.project(camera);
  vector.x = vector.x * widthHalf + widthHalf;
  vector.y = -(vector.y * heightHalf) + heightHalf;
  return vector;
};

const getPoint3 = (point2: Vector3, camera: Camera, size: Size, zValue: number = 1) => {
  const vector = tV0.set(
    (point2.x / size.width) * 2 - 1,
    -(point2.y / size.height) * 2 + 1,
    zValue
  );
  vector.unproject(camera);
  return vector;
};

export const calculateScaleFactor = (
  point3: Vector3,
  radiusPx: number,
  camera: Camera,
  size: Size
) => {
  const point2 = getPoint2(tV2.copy(point3), camera, size);
  let scale = 0;
  for (let i = 0; i < 2; ++i) {
    const point2off = tV1.copy(point2).setComponent(i, point2.getComponent(i) + radiusPx);
    const point3off = getPoint3(point2off, camera, size, point2off.z);
    scale = Math.max(scale, point3.distanceTo(point3off));
  }
  return scale;
};

const mL0 = new Matrix4();
const mW0 = new Matrix4();
const mP = new Matrix4();
const mPInv = new Matrix4();
const mW = new Matrix4();
const mL = new Matrix4();
const mL0Inv = new Matrix4();
const mdL = new Matrix4();

const bb = new Box3();
const bbObj = new Box3();
const vCenter = new Vector3();
const vSize = new Vector3();
const vAnchorOffset = new Vector3();
const vPosition = new Vector3();

const xDir = new Vector3(1, 0, 0);
const zDir = new Vector3(0, 0, 1);

export type PivotControlsProps = {
  /** Scale of the gizmo, 1 */
  scale?: number;
  /** Width of the gizmo lines, this is a Line2 prop, 2.5 */
  lineWidth?: number;
  /** If fixed is true is remains constant in size, scale is now in pixels, false */
  fixed?: boolean;
  /** Pivot does not act as a group, it won't shift contents but can offset in position */
  offset?: [number, number, number];
  /** Starting rotation */
  rotation?: [number, number, number];

  /** Starting matrix */
  matrix?: Matrix4;
  /** BBAnchor, each axis can be between -1/0/+1 */
  anchor?: [number, number, number];
  /** If autoTransform is true, automatically apply the local transform on drag, true */
  autoTransform?: boolean;
  /** Allows you to switch individual axes off */
  activeAxes?: [boolean, boolean, boolean];

  disableAxes?: boolean;
  disableSliders?: boolean;
  disableRotations?: boolean;

  /** Limits */
  translationLimits?: [
    [number, number] | undefined,
    [number, number] | undefined,
    [number, number] | undefined
  ];
  rotationLimits?: [
    [number, number] | undefined,
    [number, number] | undefined,
    [number, number] | undefined
  ];

  /** RGB colors */
  axisColors?: [string | number, string | number, string | number];
  /** Color of the hovered item */
  hoveredColor?: string | number;
  /** HTML value annotations, default: false */
  annotations?: boolean;
  /** CSS Classname applied to the HTML annotations */
  annotationsClass?: string;
  /** Drag start event */
  onDragStart?: (props: OnDragStartProps) => void;
  /** Drag event */
  onDrag?: (l: Matrix4, deltaL: Matrix4, w: Matrix4, deltaW: Matrix4) => void;
  /** Drag end event */
  onDragEnd?: () => void;
  /** Set this to false if you want the gizmo to be visible through faces */
  depthTest?: boolean;
  opacity?: number;
  visible?: boolean;
  userData?: { [key: string]: any };
  objects?: Object3D[];
  rorationSnap?: number;
  font?: string;
};

export const PivotControls = forwardRef<Group, PivotControlsProps>(
  (
    {
      matrix,
      onDragStart,
      onDrag,
      onDragEnd,
      autoTransform = true,
      anchor,
      offset = [0, 0, 0],
      rotation = [0, 0, 0],
      scale = 1,
      lineWidth = 4,
      fixed = false,
      translationLimits = [undefined, [0, 0], undefined],
      rotationLimits = [[0, 0], undefined, [0, 0]],
      depthTest = true,
      axisColors = ['#ff2060', '#20df80', '#2080ff'],
      hoveredColor = 'orange',
      annotations = true,
      annotationsClass,
      opacity = 0.8,
      visible = true,
      userData,
      font,
      ...props
    },
    fRef
  ) => {
    const invalidate = useThree((state) => state.invalidate);
    const camera = useThree((state) => state.camera);
    const parentRef = useRef<Group>(null!);
    const ref = useRef<Group>(null!);
    const annotationRef = useRef<Group>(null!);
    const gizmoRef = useRef<Group>(null!);
    const translation = useRef<[number, number, number]>([0, 0, 0]);
    const [degrees, setDegrees] = useState('0º');
    const [, startTransition] = useTransition();
    const annotationBillboardRef = useRef<Group>(null!);
    const dragComponent = useRef<'Arrow' | 'Rotator' | 'Slider' | 'Sphere'>('Slider');

    const group = useMemo(() => new Group(), []);

    useLayoutEffect(() => {
      if (!anchor) return;
      group.updateWorldMatrix(true, true);

      mPInv.copy(group.matrixWorld).invert();
      bb.makeEmpty();
      group.traverse((obj: any) => {
        if (!obj.geometry) return;
        if (!obj.geometry.boundingBox) obj.geometry.computeBoundingBox();
        mL.copy(obj.matrixWorld).premultiply(mPInv);
        bbObj.copy(obj.geometry.boundingBox);
        bbObj.applyMatrix4(mL);
        bb.union(bbObj);
      });
      vCenter.copy(bb.max).add(bb.min).multiplyScalar(0.5);
      vSize.copy(bb.max).sub(bb.min).multiplyScalar(0.5);
      vAnchorOffset
        .copy(vSize)
        .multiply(new Vector3(...anchor))
        .add(vCenter);
      vPosition.set(...offset).add(vAnchorOffset);
      gizmoRef.current.position.copy(vPosition);
      invalidate();
    });

    const config = useMemo(
      () => ({
        onDragStart: (props: OnDragStartProps) => {
          dragComponent.current = props.component;
          mL0.copy(ref.current.matrix);
          mW0.copy(ref.current.matrixWorld);
          onDragStart && onDragStart(props);
          invalidate();
        },
        onDrag: (mdW: Matrix4) => {
          mP.copy(parentRef.current.matrixWorld);
          mPInv.copy(mP).invert();
          // After applying the delta
          mW.copy(mW0).premultiply(mdW);
          mL.copy(mW).premultiply(mPInv);
          mL0Inv.copy(mL0).invert();
          mdL.copy(mL).multiply(mL0Inv);
          annotationRef.current.matrix.copyPosition(mL);
          if (autoTransform) ref.current.matrix.copy(mL);
          startTransition(() => {
            if (dragComponent.current === 'Rotator') {
              setDegrees(`${(new Quaternion().setFromRotationMatrix(mL).y * 180).toFixed(0)}º`);
            } else {
              const pos = new Vector3().setFromMatrixPosition(mL);
              setDegrees(`X: ${pos.x.toFixed(2)}m\nY: ${pos.z.toFixed(2)}m`);
            }
          });
          onDrag && onDrag(mL, mdL, mW, mdW);
          invalidate();
        },
        onDragEnd: () => {
          if (onDragEnd) onDragEnd();
          invalidate();
        },
        translation,
        translationLimits,
        rotationLimits,
        axisColors,
        hoveredColor,
        opacity,
        scale,
        lineWidth,
        fixed,
        depthTest,
        userData,
        annotations,
        annotationsClass,
      }),
      [
        translationLimits,
        rotationLimits,
        axisColors,
        hoveredColor,
        opacity,
        scale,
        lineWidth,
        fixed,
        depthTest,
        userData,
        annotations,
        annotationsClass,
        onDragStart,
        invalidate,
        autoTransform,
        onDrag,
        onDragEnd,
      ]
    );

    const vec = new Vector3();
    useFrame((state) => {
      const billboardQuaternion = camera.quaternion.clone();
      //
      billboardQuaternion.set(0, billboardQuaternion.y, 0, billboardQuaternion.w);
      annotationBillboardRef.current.quaternion.slerp(billboardQuaternion, 0.1);

      if (fixed) {
        const sf = calculateScaleFactor(
          gizmoRef.current.getWorldPosition(vec),
          scale,
          state.camera,
          state.size
        );
        if (gizmoRef.current) {
          if (
            gizmoRef.current?.scale.x !== sf ||
            gizmoRef.current?.scale.y !== sf ||
            gizmoRef.current?.scale.z !== sf
          ) {
            gizmoRef.current.scale.setScalar(sf);
            annotationBillboardRef.current.scale.setScalar(sf);
            state.invalidate();
          }
        }
      }
    });

    useImperativeHandle(fRef, () => ref.current, []);

    useLayoutEffect(() => {
      // If the matrix is a real matrix4 it means that the user wants to control the gizmo
      // In that case it should just be set, as a bare prop update would merely copy it
      if (matrix && matrix instanceof Matrix4) ref.current.matrix = matrix;
    }, [matrix]);
    return (
      <context.Provider value={config}>
        <group ref={parentRef} visible={visible}>
          <group ref={annotationRef} matrixAutoUpdate={false}>
            <group ref={annotationBillboardRef}>
              <Text
                anchorX="left"
                anchorY="middle"
                characters="01234567890º:XY\s\n"
                color="#3f3e3e"
                depthOffset={-10000}
                font={font}
                fontSize={0.25}
                letterSpacing={-0.05}
                position={[1.1, 0, 0]}
                rotation={[-Math.PI / 2, 0, 0]}
                // fillOpacity={0.7}s
              >
                {degrees}
              </Text>
            </group>
          </group>
          <group ref={ref} matrix={matrix} matrixAutoUpdate={false} {...props}>
            <group ref={gizmoRef} position={offset} rotation={rotation}>
              <AxisArrow axis={0} direction={new Vector3(1, 0, 0)} />
              <AxisArrow axis={0} direction={new Vector3(-1, 0, 0)} />
              <AxisArrow axis={2} direction={new Vector3(0, 0, 1)} />
              <AxisArrow axis={2} direction={new Vector3(0, 0, -1)} />
              <PlaneSlider axis={1} dir1={zDir} dir2={xDir} />
              <AxisRotator axis={1} dir1={zDir} dir2={xDir} />
            </group>
          </group>
        </group>
      </context.Provider>
    );
  }
);

export default PivotControls;
